import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams, HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { environment } from './../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly baseUrl = environment.apiUrl;
  private readonly defaultHeaders = new HttpHeaders({ Accept: 'application/json' });

  constructor(private httpClient: HttpClient) {}

  private handleError(error: any): Observable<never> {
    return throwError(() => error);
  }

  private getJsonHeaders(): HttpHeaders {
    return this.defaultHeaders.set('Content-Type', 'application/json');
  }

  get(path: string, params: HttpParams = new HttpParams(), retryCount = 0): Observable<any> {
    return this.httpClient
      .get(`${this.baseUrl}${path}`, { headers: this.getJsonHeaders(), params })
      .pipe(retry(retryCount), catchError(e => this.handleError(e)));
  }

  post(path: string, body: any = {}): Observable<any> {
    const headers = body instanceof FormData ? this.defaultHeaders : this.getJsonHeaders();
    return this.httpClient
      .post(`${this.baseUrl}${path}`, body, { headers })
      .pipe(catchError(e => this.handleError(e)));
  }

  put(path: string, body: any = {}): Observable<any> {
    return this.httpClient
      .put(`${this.baseUrl}${path}`, body, { headers: this.getJsonHeaders() })
      .pipe(catchError(e => this.handleError(e)));
  }

  patch(path: string, body: any = {}): Observable<any> {
    return this.httpClient
      .patch(`${this.baseUrl}${path}`, body, { headers: this.getJsonHeaders() })
      .pipe(catchError(e => this.handleError(e)));
  }

  delete(path: string, params: HttpParams = new HttpParams(), body?: any): Observable<any> {
    return this.httpClient
      .delete(`${this.baseUrl}${path}`, { headers: this.getJsonHeaders(), params, body })
      .pipe(catchError(e => this.handleError(e)));
  }

  // multipart upload: the browser sets the Content-Type boundary itself
  upload(path: string, fileData: FormData): Observable<any> {
    return this.httpClient
      .post(`${this.baseUrl}${path}`, fileData, { headers: this.defaultHeaders })
      .pipe(catchError(e => this.handleError(e)));
  }

  getBlob(path: string): Observable<Blob> {
    return this.httpClient
      .get(`${this.baseUrl}${path}`, { headers: this.defaultHeaders, responseType: 'blob' })
      .pipe(catchError(e => this.handleError(e)));
  }

  postBlob(path: string, body: any = {}): Observable<Blob> {
    return this.httpClient
      .post(`${this.baseUrl}${path}`, body, { headers: this.getJsonHeaders(), responseType: 'blob' })
      .pipe(catchError(e => this.handleError(e)));
  }
}
